'use client';
import React, { useState, useRef, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Lightbulb } from 'lucide-react';
import Image from 'next/image';
import Card from './card';

function useIsMobile(breakpoint = 640) {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < breakpoint);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, [breakpoint]);

  return isMobile;
}

const History = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();

  const historyData = [
    {
      year: '1891',
      title: 'GE Peterborough Established',
      description: "Canadian General Electric's (CGE) first international expansion opens in Peterborough, laying the foundation for more than 130 years of continuous operation in Canada. Community and civic engagement became a hallmark of the plant's culture, with CGE and the City growing together over the course of decades.",
      image: '/history/1891.png'
    },
    {
      year: '1893',
      title: "Peterborough's Electric Street Railway",
      description: "Canadian General Electric helps modernize Peterborough by building the City's first electric street railway. The system improved community connectivity, enabling easier access to work, school, and social opportunities.",
      image: '/history/1893.png'
    },
    {
      year: '1918',
      title: 'Wartime Efforts & Charitable Contributions',
      description: 'During WWI, Canadian General Electric raised a regiment detachment of 25 mechanical and electrical engineers for government service to assist with overseas operations efforts alongside the Royal Canadian Engineers. The company also contributed to organizations like the Red Cross, and was a major subscriber to the Dominion of Canada War Loan.',
      image: '/history/1918.png'
    },
    {
      year: '1927',
      title: 'Peterborough Plant Sport & Athletic Association',
      description: 'Canadian General Electric founds a sport and social club that hosts events such as picnics, sports tournaments, galas, and other events to help foster a sense of belonging among families. Initiatives like these helped build connections beyond the workplace and out in the community. Today, the Peterborough and District Sports Hall of Fame displays a number of artifacts from athletic teams associated with the Association.',
      image: '/history/1927.png'
    },
    {
      year: '1931',
      title: ' Kawartha Golf & Country Club',
      description: 'To provide affordable and accessible recreation, Canadian General Electric creates the Kawartha Golf and Country Club, designed by renowned architect, Stanley Thompson. The Club remains a vibrant community hub to this day, fostering lasting social connections between Peterborough residents.',
      image: '/history/1931.png'
    },
    {
      year: '1940',
      title: 'The Blue Coverall Brigade',
      description: 'During WWII, women joined the workforce in unprecedented numbers, entering into traditionally male dominated roles. Canadian General Electric became a catalyst for lasting social change, advancing early feminism by enabling women to contribute meaningfully to industrial production. Known as the “Blue Coverall Brigade”, these women were trailblazers in expanding the bounds of work for women in Canada.',
      image: '/history/1940.png'
    },
    {
      year: '1954',
      title: 'Kawartha Credit Union',
      description: 'Canadian General Electric helps create and grow what today is known as the Kawartha Credit Union (originally Cangeco), expanding financial services to anyone living or working in Peterborough. Today, the Kawartha Credit Union serves over 50,000 members with online and mobile banking access, with a network of 23 branches extending from Cornwall to Parry Sound.',
      image: '/history/1954.png'
    },
    {
      year: '1955',
      title: 'Nuclear Power Innovation',
      description: 'Canadian General Electric, in partnership with Atomic Energy of Canada Limited and the Hydro Electric Power Commission of Ontario, brings nuclear energy to Canada for the first time through the construction of a nuclear power station at Chalk River. Known as the Nuclear Power Demonstration, the project served as a proving ground and prototype for the CANDU reactor.',
      image: '/history/1955.png'
    },
    {
      year: '1962',
      title: 'Land Donation to Trent University',
      description: 'Canadian General Electric donates over 100 acres of land to create the core campus of Trent University. Today the University has over 13,000 students enrolled, and is ranked #1 in Ontario for student services. CGE also donated the Nassau Mills Hydro Generating Station, providing electricity to the University at a significant cost saving, and contributing to student learning for generations to come. Since then, the University has upgraded the generating equipment to modern standards, continuing to generate power that fuels the learning of thousands of students each year.',
      image: '/history/1962.png'
    },
    {
      year: '1963',
      title: 'Major Gift to Trent University',
      description: 'Canadian General Electric and its employees donate $300,000 (equivalent to $500,000 today), to the founding of Trent University, the largest donation on behalf of industrial employees to a Canadian university at the time. Today, the University is a top-ranked academic institution, with over 13,000 students enrolled.',
      image: '/history/1963.png'
    },
    {
      year: '1980',
      title: 'Partnership with United Way',
      description: 'In the 1980s, GE becomes the largest single-source donor to United Way funded agencies in Peterborough, contributing to major projects like the YMCA, and securing new hospital equipment, such as a CAT Scan machine for Civic Hospital.',
      image: '/history1.png'
    },
    {
      year: '2001',
      title: 'Indigenous Student Awards',
      description: 'General Electric pledges a one-time donation of $116,250 (equivalent to approximately $195,000 today), to create seven new undergraduate awards to support Indigenous students enrolled in Computer and / or Administrative studies at Trent University, reinforcing the importance of equitable and inclusive access to education.',
      image: '/history/2001.png'
    },
    {
      year: '2015',
      title: 'Emergency Support to United Way',
      description: 'General Electric donates $180,729 to the United Way when the charitable organization found itself at risk of not meeting their annual campaign funding goal. This donation helped the United Way in their efforts to improve people’s quality of life in the City and County of Peterborough, reducing poverty and building strong and healthy communities for all.',
      image: '/history/2015.png'
    },
    {
      year: 'Current',
      title: 'Carrying on the Legacy',
      description: 'The legacy of the large electric motor and generator product line that was developed in Peterborough continues through the ongoing operations of the GE Vernova engineering and commercial team’s that continue to work at the site. GE Vernova became a stand-alone, publicly traded company on April 2, 2024, after being spun off from General Electric (GE).',
      image: '/history/2025.webp'
    }
  ];

  const specialImagePositions: { [key: string]: string } = {
    '2001': 'center top',
    '1940': 'center top',
    '1962': 'center bottom',
    '1963': 'center top',
    '1931': 'center top',
    // Add more as needed
  };

  const checkScroll = () => {
    const container = scrollRef.current;
    if (container) {
      setAtStart(container.scrollLeft === 0);
      setAtEnd(container.scrollLeft + container.offsetWidth >= container.scrollWidth - 1);
    }
  };

  useEffect(() => {
    const container = scrollRef.current;
    if (container) {
      container.addEventListener('scroll', checkScroll);
      checkScroll();
    }
    return () => {
      if (container) container.removeEventListener('scroll', checkScroll);
    };
  }, []);

  useEffect(() => {
    setAtStart(currentSlide === 0);
    setAtEnd(currentSlide === historyData.length - 1);
  }, [currentSlide, historyData.length]);

  const nextSlide = () => {
    if (!atEnd) {
      if (scrollRef.current) {
        const card = scrollRef.current.querySelector('.history-card') as HTMLElement;
        if (card) {
          scrollRef.current.scrollBy({ left: card.offsetWidth + 24, behavior: 'smooth' }); 
        }
      }
      setCurrentSlide((prev) => Math.min(prev + 1, historyData.length - 1));
    }
  };

  const prevSlide = () => {
    if (!atStart) {
      if (scrollRef.current) {
        const card = scrollRef.current.querySelector('.history-card') as HTMLElement;
        if (card) {
          scrollRef.current.scrollBy({ left: -card.offsetWidth - 24, behavior: 'smooth' });
        }
      }
      setCurrentSlide((prev) => Math.max(prev - 1, 0));
    }
  };

  return (
    <section id="history" className=" md:py-20 bg-[#004746] text-[#CDE4E4] min-h-screen">
      <div className="container mx-auto md:px-3 max-w-8xl">
        <div className="mb-10 md:mb-20 relative px-4 md:px-0">
      { isMobile && <Card/> }
          <h3 id="intro-heading" className="text-2xl md:text-5xl uppercase font-extrabold mb-4 md:mb-6 py-10 md:py-0 pb-0">
            OUR HISTORY
          </h3>
          <h2 className="hidden md:block text-2xl md:text-4xl lg:text-5xl font-mediumw leading-tight mb-6 md:mb-8">
            Creating a Better World with<br />
            the Peterborough Community
          </h2>
          <h2 className="block md:hidden text-2xl md:text-4xl lg:text-5xl font-mediumw leading-tight mb-6 md:mb-8">
            Creating a Better World<br />with
            the Peterborough<br />
            Community
          </h2>
          {/* Flex container for text and chevron buttons */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <p className="text-sm md:text-base leading-relaxed max-w-4xl">
            General Electric established its first global expansion plant in Peterborough in 1892, launching a long-standing relationship with Canada and the community that would span over 130 years. Since then, the plant has played an important role in shaping the City’s identity over the decades, and helping to establish Canada as a global leader in the energy space.
            </p>
            <div className="hidden lg:flex justify-end gap-2 flex-shrink-0 mt-10">
              <button
                onClick={prevSlide}
                className={`w-10 h-10 rounded-full flex items-center cursor-pointer justify-center transition-colors ${atStart ? 'bg-[#C3D3D3] cursor-not-allowed' : 'bg-[#C8FF08] hover:bg-[#002D2E]'}`}
                disabled={atStart}
              >
                <ChevronLeft className={`w-5 h-5 ${atStart ? 'text-[#7A9292]' : 'text-[#005E60] hover:text-white'}`} />
              </button>
              <button
                onClick={nextSlide}
                className={`w-10 h-10 rounded-full flex items-center justify-center cursor-pointer transition-colors ${atEnd ? 'bg-[#C3D3D3] cursor-not-allowed' : 'bg-[#C8FF08] hover:bg-[#002D2E]'}`}
                disabled={atEnd}
              >
                <ChevronRight className={`w-5 h-5 ${atEnd ? 'text-[#7A9292]' : 'text-[#005E60] hover:text-white'}`} />
              </button>
            </div>
          </div>
        </div>


        {/* Desktop Layout */}
        <div className="hidden lg:block md:block">
          <div ref={scrollRef} className="flex overflow-x-auto flex-nowrap gap-6 py-2 scrollbar-thin scrollbar-thumb-[#C8FF08] snap-x snap-mandatory">
            {historyData.map((item, idx) => (
              <div 
                key={item.year + '-' + idx}
                className="history-card min-w-[485px] max-w-[350px] bg-white rounded-lg overflow-hidden shadow-md relative flex-shrink-0 snap-start"
              >
                <div className="relative">
                  <div className="absolute top-0 left-1/2 transform -translate-x-1/2 h-12 w-1z-0" />
                  <div className="h-10 bg-[#D4F2F2]" />
                  <div className="absolute top-5 left-1/2 transform -translate-x-1/2 z-10 w-12 h-12 bg-[#005E60] rounded-full flex items-center justify-center shadow-md">
                    <Image src="/bulb.svg" alt="Bulb" width={24} height={24} className="w-6 h-6 object-contain" />
                  </div>
                </div>
                <div className="pt-10 flex justify-center">
                  <div className="w-[90%] rounded-lg overflow-hidden">
                    {/* Updated to 16:9 aspect ratio */}
                    <div className="relative w-full aspect-[16/9] overflow-hidden rounded-md">
                      <Image
                        src={item.image}
                        alt={item.title}
                        fill
                        sizes="(max-width: 768px) 85vw, (max-width: 1024px) 50vw, 350px"
                        className="object-cover filter grayscale"
                        style={{ 
                          objectPosition: specialImagePositions[item.year as keyof typeof specialImagePositions] || 'center center' 
                        }}
                      />
                    </div>
                  </div>
                </div>
                <div className="px-6 pb-6 pt-6 text-center">
                  <h3 id="intro-heading" className="text-4xl md:text-5xl font-extrabold text-[#005E60] mb-2">
                    {item.year}
                  </h3>
                  <h4 className="text-lg text-black mb-2">
                    {item.title}
                  </h4>
                  <p className="text-sm text-gray-700 leading-relaxed">
                    {item.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Tablet Layout */}
        <div className="hidden md:hidden">
          <div className="flex justify-end gap-2 mb-6">
            <button
              onClick={prevSlide}
              className={`w-10 h-10 rounded-full flex items-center cursor-pointer justify-center transition-colors ${atStart ? 'bg-[#C3D3D3] cursor-not-allowed' : 'bg-[#C8FF08] hover:bg-[#6BA315]'}`}
              disabled={atStart}
            >
              <ChevronLeft className={`w-5 h-5 ${atStart ? 'text-[#7A9292]' : 'text-gray-500'}`} />
            </button>
            <button
              onClick={nextSlide}
              className={`w-10 h-10 rounded-full flex items-center justify-center cursor-pointer transition-colors ${atEnd ? 'bg-[#C3D3D3] cursor-not-allowed' : 'bg-[#C8FF08] hover:bg-[#6BA315]'}`}
              disabled={atEnd}
            >
              <ChevronRight className={`w-5 h-5 ${atEnd ? 'text-[#7A9292]' : 'text-gray-500'}`} />
            </button>
          </div>
          <div className="relative overflow-hidden">
            <div
              className="flex transition-transform duration-300 ease-in-out"
              style={{ transform: `translateX(-${currentSlide * 50}%)` }}
            >
              {historyData.map((item, idx) => (
                <div key={item.year + '-' + idx} className="w-1/2 flex-shrink-0 px-3">
                  <div className="bg-[#E8F4F8] rounded-lg overflow-hidden">
                    <div className="p-6 pb-4">
                      <div className="flex justify-center mb-4">
                        <div className="w-12 h-12 bg-[#2B5C5E] rounded-full flex items-center justify-center">
                          <Lightbulb className="w-6 h-6 text-white" />
                        </div>
                      </div>
                      {/* Updated to 16:9 aspect ratio */}
                      <div className="relative w-full aspect-[16/9] mb-4 overflow-hidden rounded">
                        <Image
                          src={item.image}
                          alt={item.title}
                          fill
                          sizes="(max-width: 768px) 50vw, 350px"
                          className="object-cover filter grayscale"
                          style={{ 
                            objectPosition: specialImagePositions.hasOwnProperty(item.year) 
                              ? specialImagePositions[item.year as keyof typeof specialImagePositions]
                              : 'center center' 
                          }}
                        />
                      </div>
                    </div>
                    <div className="px-6 pb-6">
                      <h3 className="text-4xl font-bold text-[#2B5C5E] text-center mb-4">
                        {item.year}
                      </h3>
                      <h4 className="text-lg font-semibold text-[#2B5C5E] text-center mb-4">
                        {item.title}
                      </h4>
                      <p className="text-sm text-[#2B5C5E] leading-relaxed text-center">
                        {item.description}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Mobile Layout */}
        <div className="block md:hidden md:ml-0 ">
          <div className="relative">
            <div className="flex overflow-x-auto flex-nowrap gap-6 md:gap-6 py-2 scrollbar-thin scrollbar-thumb-[#C8FF08] snap-mandatory pb-14">
              {historyData.map((item, idx) => (
                <div
                  key={item.year + '-' + idx}
                  className={`history-card min-w-[85vw] max-w-[85vw] bg-white rounded-lg overflow-hidden shadow-md snap-start ${idx === 0 ? "ml-5" : ""}`}
                >
                  <div className="relative">
                    <div className="absolute top-0 left-1/2 transform -translate-x-1/2 h-12 w-1z-0" />
                    <div className="h-10 bg-[#D4F2F2]" />
                    <div className="absolute top-5 left-1/2 transform -translate-x-1/2 z-10 w-12 h-12 bg-[#005E60] rounded-full flex items-center justify-center shadow-md">
                      <Image src="/bulb.svg" alt="Bulb" width={24} height={24} className="w-6 h-6 object-contain" />
                    </div>
                  </div>
                  <div className="pt-10 flex justify-center">
                    <div className="w-[90%] rounded-lg overflow-hidden">
                      {/* Updated to 16:9 aspect ratio */}
                      <div className="relative w-full aspect-[16/9] overflow-hidden rounded-md">
                        <Image
                          src={item.image}
                          alt={item.title}
                          fill
                          sizes="85vw"
                          className="object-cover filter grayscale"
                          style={{ 
                            objectPosition: specialImagePositions[item.year as keyof typeof specialImagePositions] || 'center center' 
                          }}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="px-6 pb-6 pt-6 text-center">
                    <h3 id="intro-heading" className="text-4xl md:text-5xl font-extrabold text-[#005E60] mb-2">
                      {item.year}
                    </h3>
                    <h4 className="text-lg text-black mb-2">
                      {item.title}
                    </h4>
                    <p className="text-sm text-gray-700 leading-relaxed">
                      {item.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default History; 
